// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import PropTypes from 'prop-types';
import Row from '@tcp/core/src/components/common/atoms/Row';
import withStyles from '../../../../../../common/hoc/withStyles';
import LoaderSkelton from '../../../../../../common/molecules/LoaderSkelton';
import styles from '../styles/BundleProductItemsSkeleton.style';
import {
  getImagesToDisplay,
  getMapSliceForColorProductId,
} from '../../../../ProductListing/molecules/ProductList/utils/productsCommonUtils';
import ProductPrice from '../../../../ProductDetail/molecules/ProductPrice/ProductPrice';
import BodyCopy from '../../../../../../common/atoms/BodyCopy';
import Col from '../../../../../../common/atoms/Col';
import ProductDetailImage from '../../../../../../common/molecules/ProductDetailImage';
import { IMG_DATA } from '../../../views/config';
import { BadgeItem } from '../../../../ProductListing/molecules/ProductList/views/ProductItemComponents';
import { parseBoolean } from '../../../../../../../utils';

const BundleCommonStructure = () => {
  return (
    <>
      <div className="product-overview-detail-wrapper">
        <div className="product-image">
          <LoaderSkelton />
        </div>
        <div className="product-title-price-wrapper">
          <div className="product-title">
            <LoaderSkelton />
          </div>
          <div className="product-price">
            <LoaderSkelton />
          </div>
          <div className="product-color">
            <LoaderSkelton />
          </div>
          <div className="product-size">
            <LoaderSkelton />
          </div>
        </div>
      </div>
      <div className="product-add-to-bag">
        <LoaderSkelton />
      </div>
    </>
  );
};

const BundleDetailsSkeletonStructure = (className) => {
  const thumbnailList = [];
  for (let itr = 0; itr < 5; itr += 1) {
    thumbnailList.push(
      <div className="thumbnail-list">
        <LoaderSkelton />
      </div>
    );
  }
  return (
    <div className={className}>
      <Row fullBleed className="product-detail-skeleton-wrapper">
        <Col colSize={{ small: 6, medium: 3, large: 6 }}>
          <LoaderSkelton className="product-skeleton-img" />
        </Col>
        <Col colSize={{ small: 6, medium: 8, large: 6 }}>{BundleCommonStructure()}</Col>
      </Row>
    </div>
  );
};

const getSingleImageObj = (uniqueId) => {
  const imagePath = `${uniqueId?.split('_')[0]}/${uniqueId}`;
  return [
    {
      isShortImage: false,
      isOnModalImage: false,
      iconSizeImageUrl: imagePath,
      listingSizeImageUrl: imagePath,
      regularSizeImageUrl: imagePath,
      bigSizeImageUrl: imagePath,
      superSizeImageUrl: imagePath,
    },
  ];
};

const BundleProductItemsSkeleton = ({
  currentProduct,
  primaryBrand,
  className,
  retrievedProductInfo,
  fromPLPPage,
  currency,
  currencyAttributes,
  isShowPriceRangeKillSwitch,
  showPriceRangeForABTest,
  isHideOriginalPrice,
}) => {
  if (!retrievedProductInfo) {
    return BundleDetailsSkeletonStructure(className);
  }
  const {
    name,
    colorFitsSizesMap,
    uniqueId,
    imagesByColor,
    listPrice,
    offerPrice,
    priceRange,
    isGiftCard,
    plpTopBadge,
  } = retrievedProductInfo;

  const currentColorEntry = getMapSliceForColorProductId(colorFitsSizesMap, uniqueId) || {};
  let imagesToDisplay = getImagesToDisplay({
    imagesByColor,
    curentColorEntry: currentColorEntry,
    isAbTestActive: false,
    isFullSet: true,
    excludeShortImage: false,
  });

  if ((Array.isArray(imagesToDisplay) && imagesToDisplay.length === 0) || !imagesToDisplay) {
    imagesToDisplay = getSingleImageObj(uniqueId);
  }

  const { miscInfo: { badge1 = {}, badge3 = '' } = {} } = currentColorEntry;

  const topBadge = plpTopBadge || badge1.defaultBadge;
  const badgeText = listPrice - offerPrice !== 0 ? badge3 : '';

  const showPriceRange =
    parseBoolean(isShowPriceRangeKillSwitch) && showPriceRangeForABTest && priceRange;

  return (
    <div className={className}>
      <Row fullBleed className="product-detail-skeleton-wrapper">
        <Col colSize={{ small: 6, medium: 3, large: 6 }}>
          <BodyCopy
            className="product-title hide-on-desktop hide-on-tablet"
            fontSize="fs18"
            component="h1"
            fontFamily="secondary"
            fontWeight="extrabold"
            itemprop="name"
          >
            {name}
          </BodyCopy>
          <ProductDetailImage
            className="outfit-carousal-image"
            imageUrl={imagesToDisplay?.[0]?.bigSizeImageUrl}
            imageName={currentProduct?.name}
            isZoomEnabled={false}
            imageConfig={IMG_DATA?.imgConfig}
            isOutfitPage={false}
            primaryBrand={primaryBrand}
            fromPLPPage={fromPLPPage}
          />
          {fromPLPPage && (
            <ProductDetailImage
              className="outfit-carousal-skeleton-image"
              imageUrl={imagesToDisplay?.[0]?.bigSizeImageUrl}
              imageName={currentProduct?.name}
              isZoomEnabled={false}
              imageConfig={IMG_DATA?.imgConfig}
              isOutfitPage={false}
              primaryBrand={primaryBrand}
              fromPLPPage={false}
            />
          )}
        </Col>
        <Col colSize={{ small: 6, medium: 5, large: 6 }}>
          <div className="product-title">
            <BadgeItem
              customFontWeight="regular"
              customFontSize={['fs10', 'fs10', 'fs10']}
              className="inline-badge-item"
              isShowBadges
              text={topBadge}
            />
            <BodyCopy
              className={`product-title hide-on-mobile`}
              fontSize="fs22"
              component="h1"
              fontFamily="secondary"
              fontWeight="extrabold"
              itemprop="name"
            >
              {name}
            </BodyCopy>
          </div>
          <div className="product-price">
            {listPrice ? (
              <ProductPrice
                listPrice={listPrice}
                offerPrice={offerPrice}
                hideItemProp
                currencySymbol={currency}
                currencyAttributes={currencyAttributes}
                isGiftCard={isGiftCard}
                priceRange={priceRange}
                badge2={badgeText}
                highOfferPrice={showPriceRange && priceRange.highOfferPrice}
                highListPrice={showPriceRange && priceRange.highListPrice}
                isNewPDPEnabled
                isHideOriginalPrice={isHideOriginalPrice}
              />
            ) : (
              <LoaderSkelton />
            )}
          </div>
          {BundleCommonStructure()}
        </Col>
      </Row>
    </div>
  );
};

BundleProductItemsSkeleton.propTypes = {
  className: PropTypes.string.isRequired,
};

export default withStyles(BundleProductItemsSkeleton, styles);
export { BundleProductItemsSkeleton as BundleProductItemsSkeletonVanilla };
